import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '@/components/seo';
import { Header, Footer } from '@/components/public';
import {
  Lock, FileText, CheckCircle2, ListChecks, Shield,
  MessageSquare, Mail, AlertTriangle, Info,
  Briefcase, Users, Clock, ArrowRight, ChevronDown,
  Upload as UploadIcon, UserPlus, Eye as EyeIcon, RefreshCw, Award, Star,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAVY_BG = 'bg-[#0f1923]';
const NAVY_DARK = 'bg-[#0a131c]';
const GREEN = 'text-[#00c896]';
const GREEN_BG = 'bg-[#00c896]';

// ─── shared subcomponents ───────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <span className={`${GREEN} font-semibold text-xs uppercase tracking-[0.18em]`}>
    {children}
  </span>
);

const TrustItem = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-3" data-testid="trust-item">
    <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-[#0f1923]" strokeWidth={1.6} />
    </div>
    <p className="text-sm text-slate-700 font-medium leading-tight">{label}</p>
  </div>
);

const ProblemCard = ({ icon: Icon, title, desc, quote }) => (
  <div className="bg-[#0a131c] border border-slate-800 rounded-xl p-7 hover:border-slate-700 transition-colors">
    <div className="w-11 h-11 rounded-lg bg-slate-800/60 flex items-center justify-center mb-5">
      <Icon className="w-5 h-5 text-slate-300" strokeWidth={1.6} />
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed mb-5">{desc}</p>
    <p className="text-xs italic text-slate-500 border-l-2 border-slate-700 pl-3">&ldquo;{quote}&rdquo;</p>
  </div>
);

const Step = ({ n, title, desc }) => (
  <div className="flex gap-4">
    <div className={`w-8 h-8 rounded-full ${GREEN_BG} text-[#0f1923] flex items-center justify-center text-sm font-bold shrink-0`}>
      {n}
    </div>
    <div className="pb-2">
      <h4 className="text-base font-semibold text-slate-900 mb-1">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#00c896] hover:shadow-md transition-all">
    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-[#00c896]" strokeWidth={1.6} />
    </div>
    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

const WhoCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-[#0a131c] border border-slate-800 rounded-xl p-6 hover:border-[#00c896]/40 transition-colors">
    <div className="w-10 h-10 rounded-lg bg-[#00c896]/10 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-[#00c896]" strokeWidth={1.6} />
    </div>
    <h3 className="text-base font-semibold text-white mb-1.5">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
  </div>
);

const SecurityCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border-t-4 border-[#00c896] border-l border-r border-b border-slate-200 rounded-xl p-6 shadow-sm">
    <div className="w-11 h-11 rounded-lg bg-emerald-50 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-[#00c896]" strokeWidth={1.6} />
    </div>
    <h3 className="text-base font-semibold text-slate-900 mb-2">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

// ─── Journey step (desktop) ─────────────────────────────────────────────────
const JourneyStep = ({ step, icon: Icon, title, desc, color = 'green', highlight, repeats }) => {
  const isAmber = color === 'amber';
  const iconWrap = highlight
    ? 'w-20 h-20 -mt-3 ring-4 ring-[#00c896]/30 shadow-[0_0_28px_rgba(0,200,150,0.45)]'
    : 'w-14 h-14';
  const wrapBg = isAmber
    ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
    : 'bg-[#00c896]/15 border-[#00c896]/40 text-[#00c896]';
  return (
    <div className="relative text-center px-2" data-testid={`journey-step-${step}`}>
      <div className={`${iconWrap} ${wrapBg} relative z-10 mx-auto rounded-full border-2 flex items-center justify-center bg-[#0f1923]`}>
        <Icon className={highlight ? 'w-8 h-8' : 'w-6 h-6'} strokeWidth={1.6} />
      </div>
      {highlight && (
        <span className="mt-3 inline-flex items-center gap-1 bg-[#00c896]/15 border border-[#00c896]/40 text-[#00c896] text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">
          <Star className="w-3 h-3" /> AUTOMATIC
        </span>
      )}
      <p className={`mt-3 text-xs font-bold tracking-wider ${isAmber ? 'text-amber-300' : 'text-[#00c896]'}`}>
        STEP {step}
      </p>
      <h3 className={`text-base font-semibold mt-1 ${highlight ? 'text-[#00c896]' : 'text-white'}`}>
        {title}
      </h3>
      <p className="text-xs text-slate-400 leading-relaxed mt-2 max-w-[15rem] mx-auto">{desc}</p>
      {repeats && (
        <p className="mt-2 text-[10px] italic text-amber-300/80 max-w-[14rem] mx-auto leading-snug inline-flex items-start gap-1">
          <RefreshCw className="w-3 h-3 mt-0.5 shrink-0" />
          This step repeats until all parties are satisfied
        </p>
      )}
    </div>
  );
};

// ─── Journey step (mobile) ──────────────────────────────────────────────────
const JourneyStepMobile = ({ step, icon: Icon, title, desc, color = 'green', highlight, repeats }) => {
  const isAmber = color === 'amber';
  const wrapBg = isAmber
    ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
    : 'bg-[#00c896]/15 border-[#00c896]/40 text-[#00c896]';
  return (
    <div className="relative pl-12" data-testid={`journey-step-mobile-${step}`}>
      <div className={`absolute left-0 top-0 ${wrapBg} w-10 h-10 rounded-full border-2 flex items-center justify-center bg-[#0f1923] z-10 ${highlight ? 'ring-2 ring-[#00c896]/40' : ''}`}>
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </div>
      <div className="bg-[#0a131c] border border-slate-800 rounded-lg p-3.5">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-[10px] font-bold tracking-wider ${isAmber ? 'text-amber-300' : 'text-[#00c896]'}`}>
            STEP {step}
          </p>
          {highlight && (
            <span className="inline-flex items-center gap-1 bg-[#00c896]/15 border border-[#00c896]/40 text-[#00c896] text-[9px] font-bold px-1.5 py-0.5 rounded-full">
              <Star className="w-2.5 h-2.5" /> AUTOMATIC
            </span>
          )}
        </div>
        <h3 className={`text-sm font-semibold mt-1 ${highlight ? 'text-[#00c896]' : 'text-white'}`}>{title}</h3>
        <p className="text-xs text-slate-400 leading-relaxed mt-1.5">{desc}</p>
        {repeats && (
          <p className="mt-2 text-[10px] italic text-amber-300/80 leading-snug">
            ↻ This step repeats until all parties are satisfied
          </p>
        )}
      </div>
    </div>
  );
};

// ─── email capture ──────────────────────────────────────────────────────────
const EmailCapture = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrMsg('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    try {
      await axios.post(`${API}/contact/data-room-pdf`, { email: trimmed });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrMsg(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div
        className="bg-[#0a131c] border border-[#00c896]/30 rounded-2xl p-8 sm:p-10 text-center"
        data-testid="data-room-capture-success"
      >
        <div className={`w-14 h-14 rounded-full ${GREEN_BG}/15 border-2 border-[#00c896] flex items-center justify-center mx-auto mb-4`}>
          <CheckCircle2 className="w-7 h-7 text-[#00c896]" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Check your inbox — the PDF is on its way.</h3>
        <p className="text-sm text-slate-400">We will be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-[#0a131c] border border-slate-800 rounded-2xl p-8 sm:p-10 text-center"
      data-testid="data-room-capture-form"
    >
      <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2">
        Download the Data Room overview
      </h3>
      <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto leading-relaxed">
        Enter your email and we will send you a one-page PDF — share it with your investors.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          disabled={status === 'loading'}
          className="flex-1 bg-[#0f1923] border border-slate-700 focus:border-[#00c896] outline-none text-white placeholder-slate-500 rounded-lg px-4 py-3 text-sm transition-colors"
          data-testid="data-room-email-input"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className={`${GREEN_BG} hover:bg-[#00b287] disabled:opacity-60 text-[#0f1923] font-semibold rounded-lg px-5 py-3 text-sm inline-flex items-center justify-center gap-1.5 transition-colors`}
          data-testid="data-room-submit-btn"
        >
          {status === 'loading' ? 'Sending…' : <>Send Me the PDF <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
      {errMsg && <p className="text-xs text-red-400 mt-3" data-testid="data-room-capture-error">{errMsg}</p>}
      <p className="text-xs text-slate-500 mt-4">We will also reach out to help you get started.</p>
    </form>
  );
};

// ─── page ───────────────────────────────────────────────────────────────────
const DataRoomPage = () => {
  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-white">
      <SEOHead
        title="StartEase Data Room — Secure Document Review for Founders & Investors | StartEase Agent"
        description="A private, audited space for founders and investors to review, comment, and approve legal documents. OTP login, version control, completion certificate. No WhatsApp."
        canonical="https://starteaseai.com/data-room"
      />
      <Header />

      {/* 1 · HERO */}
      <section className={`${NAVY_BG} relative overflow-hidden`} data-testid="data-room-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1923] via-[#13202d] to-[#0f1923]" />
        <div className="relative max-w-5xl mx-auto px-6 py-24 sm:py-32 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00c896]/40 bg-[#00c896]/10 text-[#00c896] text-xs font-semibold tracking-wide mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00c896] animate-pulse" />
            NEW · StartEase Data Room
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
            Your deal documents.<br />
            Reviewed securely.<br />
            <span className="text-[#00c896]">Approved with confidence.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            A private, audited space for founders and investors to review, comment, and approve
            legal documents — without WhatsApp or email chains.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={scrollTo('contact')}
              className={`${GREEN_BG} hover:bg-[#00b287] text-[#0f1923] font-semibold px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors`}
              data-testid="hero-cta-primary"
            >
              Talk to an Advisor <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={scrollTo('how')}
              className="text-slate-200 hover:text-white border border-slate-600 hover:border-slate-400 px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors"
              data-testid="hero-cta-secondary"
            >
              See How It Works <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* 2 · TRUST BAR */}
      <section className="bg-slate-100 border-y border-slate-200" data-testid="data-room-trust">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-5 gap-6">
          <TrustItem icon={Lock} label="OTP Login — No Passwords" />
          <TrustItem icon={FileText} label="Version Control" />
          <TrustItem icon={CheckCircle2} label="One-Click Approval" />
          <TrustItem icon={ListChecks} label="Full Audit Trail" />
          <TrustItem icon={Shield} label="AWS Encrypted Storage" />
        </div>
      </section>

      {/* 3 · PROBLEM */}
      <section className={`${NAVY_BG} py-24`} data-testid="data-room-problem">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>THE PROBLEM</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">The old way is broken</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <ProblemCard
              icon={MessageSquare}
              title="WhatsApp Chains"
              desc="Documents shared over chat with no record of who approved what or when."
              quote="version 3 final FINAL revised.pdf"
            />
            <ProblemCard
              icon={Mail}
              title="Email Threads"
              desc="Did they even open it? Which version did they review? No way to know."
              quote="Re: Re: Re: Updated Agreement"
            />
            <ProblemCard
              icon={AlertTriangle}
              title="No Audit Trail"
              desc="If a dispute happens — you have nothing to show. No timestamps, no record."
              quote="I never approved that version."
            />
          </div>
        </div>
      </section>

      {/* 4 · HOW IT WORKS */}
      <section id="how" className="bg-slate-100 py-24" data-testid="data-room-how">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>HOW IT WORKS</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Two sides. One secure room.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 md:divide-x md:divide-slate-300">
            <div className="space-y-6 md:pr-8">
              <p className={`${GREEN} font-semibold text-sm uppercase tracking-wide`}>For the Founder</p>
              <Step n={1} title="Upload the document"
                    desc="Upload your agreement. Add reviewer instructions — Please check Clause 4.2." />
              <Step n={2} title="Add parties"
                    desc="Tag investors and advisors by email. They receive a secure invite instantly." />
              <Step n={3} title="Track responses"
                    desc="See who approved, who requested changes, and who has not opened it." />
              <Step n={4} title="Export audit trail"
                    desc="Download a branded PDF showing complete history. Legally defensible." />
            </div>
            <div className="space-y-6 md:pl-8">
              <p className={`${GREEN} font-semibold text-sm uppercase tracking-wide`}>For the Investor</p>
              <Step n={1} title="Receive email invite"
                    desc="One email. No account creation. No password to remember." />
              <Step n={2} title="Log in with OTP"
                    desc="Go to starteaseai.com/vdr/login. Enter email. Receive 6-digit code." />
              <Step n={3} title="Review and respond"
                    desc="Read document. See instructions. Approve or request changes." />
              <Step n={4} title="Response is locked"
                    desc="Timestamp recorded. Response locked. Full legal record created." />
            </div>
          </div>
        </div>
      </section>

      {/* 4.5 · JOURNEY (between How It Works and Features) */}
      <section className={`${NAVY_BG} py-24 relative overflow-hidden`} data-testid="data-room-journey">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1923] via-[#13202d] to-[#0f1923]" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <SectionLabel>THE JOURNEY</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">
              From upload to certificate — six steps
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-400">
              Every deal follows the same secure path.
            </p>
          </div>

          {/* Desktop horizontal timeline */}
          <div className="hidden lg:grid grid-cols-6 gap-4 relative">
            {/* Connector line */}
            <div className="absolute top-9 left-[8.33%] right-[8.33%] h-px bg-gradient-to-r from-[#00c896]/40 via-[#00c896]/40 to-[#00c896]/80 pointer-events-none" />
            {[
              { icon: UploadIcon, title: 'Upload',
                desc: 'Admin uploads the document and adds specific instructions for reviewers.',
                color: 'green' },
              { icon: UserPlus, title: 'Invite',
                desc: 'Parties are invited by email. OTP login — no password, no account needed.',
                color: 'green' },
              { icon: EyeIcon, title: 'Review',
                desc: 'Each party reads the document and sees the reviewer instructions.',
                color: 'green' },
              { icon: CheckCircle2, title: 'Respond',
                desc: 'Approve or request changes. Response is locked with a timestamp instantly.',
                color: 'green' },
              { icon: RefreshCw, title: 'Revise (if needed)',
                desc: 'If changes requested — admin uploads a new version. All parties re-approve.',
                color: 'amber', repeats: true },
              { icon: Award, title: 'Certificate', highlight: true,
                desc: 'When all parties approve — a branded certificate is generated automatically and sent to everyone.',
                color: 'green' },
            ].map((step, i) => (
              <JourneyStep key={step.title} step={i + 1} {...step} />
            ))}
          </div>

          {/* Mobile vertical timeline */}
          <div className="lg:hidden space-y-5 relative">
            <div className="absolute top-2 bottom-2 left-[20px] w-px bg-gradient-to-b from-[#00c896]/30 via-[#00c896]/30 to-[#00c896]/80 pointer-events-none" />
            {[
              { icon: UploadIcon, title: 'Upload',
                desc: 'Admin uploads the document and adds specific instructions for reviewers.', color: 'green' },
              { icon: UserPlus, title: 'Invite',
                desc: 'Parties are invited by email. OTP login — no password, no account needed.', color: 'green' },
              { icon: EyeIcon, title: 'Review',
                desc: 'Each party reads the document and sees the reviewer instructions.', color: 'green' },
              { icon: CheckCircle2, title: 'Respond',
                desc: 'Approve or request changes. Response is locked with a timestamp instantly.', color: 'green' },
              { icon: RefreshCw, title: 'Revise (if needed)',
                desc: 'If changes requested — admin uploads a new version. All parties re-approve.',
                color: 'amber', repeats: true },
              { icon: Award, title: 'Certificate', highlight: true,
                desc: 'When all parties approve — a branded certificate is generated automatically and sent to everyone.',
                color: 'green' },
            ].map((step, i) => (
              <JourneyStepMobile key={step.title} step={i + 1} {...step} />
            ))}
          </div>
        </div>
      </section>

      {/* 5 · FEATURES */}
      <section className="bg-white py-24" data-testid="data-room-features">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>FEATURES</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
              Everything you need.<br />Nothing you don&apos;t.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard icon={Lock} title="OTP Login — No Passwords"
              desc="Investors log in with a 6-digit code. No account, no password." />
            <FeatureCard icon={FileText} title="Version Control"
              desc="Every draft preserved forever. Old comments never deleted." />
            <FeatureCard icon={CheckCircle2} title="One-Click Approval"
              desc="Approve or request changes in seconds. Locked with timestamp instantly." />
            <FeatureCard icon={MessageSquare} title="Threaded Comments"
              desc="Discuss clauses directly on document. No email chains needed." />
            <FeatureCard icon={ListChecks} title="Full Audit Trail"
              desc="Every action logged permanently. Export as branded PDF." />
            <FeatureCard icon={Info} title="Reviewer Instructions"
              desc="Guide reviewers on what to check. Shown highlighted before reviewing." />
          </div>
        </div>
      </section>

      {/* 6 · WHO USES IT */}
      <section className={`${NAVY_BG} py-24`} data-testid="data-room-who">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>WHO USES IT</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">Built for investment deals</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <WhoCard icon={Briefcase} title="Investors"
              desc="Review term sheets, SAFEs, and shareholder agreements." />
            <WhoCard icon={Users} title="Co-Founders"
              desc="Align on equity, vesting, and founder agreements." />
            <WhoCard icon={Shield} title="Legal Counsel"
              desc="Review and approve filings with full version history." />
            <WhoCard icon={Clock} title="Advisors"
              desc="Comment and approve — all in one place." />
          </div>
        </div>
      </section>

      {/* 7 · SECURITY */}
      <section className="bg-slate-100 py-24" data-testid="data-room-security">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>SECURITY</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Built for legal confidence</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <SecurityCard icon={Shield} title="Encrypted Storage"
              desc="AWS S3 encrypted storage. Presigned URLs expire in 15 minutes. Raw file paths never exposed." />
            <SecurityCard icon={Clock} title="Tamper-Proof Log"
              desc="Activity log is append-only. Nothing can be deleted or modified. Every action is permanent." />
            <SecurityCard icon={FileText} title="Legal Grade Export"
              desc="Download branded PDF audit report showing complete history — who viewed, commented, and approved, when." />
          </div>
        </div>
      </section>

      {/* 8 · EMAIL CAPTURE */}
      <section id="contact" className={`${NAVY_DARK} py-24`} data-testid="data-room-contact">
        <div className="max-w-2xl mx-auto px-6">
          <EmailCapture />
        </div>
      </section>

      {/* 9 · CTA */}
      <section className="bg-[#f4f6f9] py-24" data-testid="data-room-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            Ready to close your deal<br />the right way?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-xl mx-auto">
            Your StartEase advisor sets up the Data Room. Your investors just need their email address.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/login"
              className={`${GREEN_BG} hover:bg-[#00b287] text-[#0f1923] font-semibold px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors`}
              data-testid="cta-primary"
            >
              Get Started with StartEase <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={scrollTo('contact')}
              className="text-slate-700 hover:text-slate-900 border border-slate-300 hover:border-slate-500 px-6 py-3 rounded-lg transition-colors"
              data-testid="cta-secondary"
            >
              Talk to an Advisor
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DataRoomPage;
